import React from 'react';
import './Calendar.css';

interface CalendarProps {
  sessions: Array<{
    id: number;
    date: string;
    time: string;
    title: string;
    status: 'upcoming' | 'past';
    hasRecording: boolean;
  }>;
  selectedDate: string | null;
  onDateSelect: (date: string) => void;
}

const Calendar: React.FC<CalendarProps> = ({ sessions, selectedDate, onDateSelect }) => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  const [displayMonth, setDisplayMonth] = React.useState(currentMonth);
  const [displayYear, setDisplayYear] = React.useState(currentYear);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDateKey = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getSessionsForDate = (dateKey: string) => {
    return sessions.filter(s => s.date === dateKey);
  };

  const isToday = (year: number, month: number, day: number) => {
    const date = new Date(year, month, day);
    return date.toDateString() === today.toDateString();
  };

  const isPast = (year: number, month: number, day: number) => {
    const date = new Date(year, month, day);
    return date < today && !isToday(year, month, day);
  };

  const daysInMonth = getDaysInMonth(displayMonth, displayYear);
  const firstDay = getFirstDayOfMonth(displayMonth, displayYear);
  const days = [];

  // Empty cells for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  const handlePrevMonth = () => {
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(displayYear - 1);
    } else {
      setDisplayMonth(displayMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
  };

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button className="calendar-nav-button" onClick={handlePrevMonth}>←</button>
        <h2 className="calendar-month-year">
          {monthNames[displayMonth]} {displayYear}
        </h2>
        <button className="calendar-nav-button" onClick={handleNextMonth}>→</button>
      </div>

      <div className="calendar-grid">
        {/* Day names header */}
        {dayNames.map((day) => (
          <div key={day} className="calendar-day-header">
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {days.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="calendar-day empty"></div>;
          }

          const dateKey = formatDateKey(displayYear, displayMonth, day);
          const daySessions = getSessionsForDate(dateKey);
          const isSelected = selectedDate === dateKey;
          const isTodayDate = isToday(displayYear, displayMonth, day);
          const isPastDate = isPast(displayYear, displayMonth, day);

          return (
            <div
              key={day}
              className={`calendar-day ${isSelected ? 'selected' : ''} ${isTodayDate ? 'today' : ''} ${isPastDate ? 'past' : ''} ${daySessions.length > 0 ? 'has-sessions' : ''}`}
              onClick={() => onDateSelect(dateKey)}
            >
              <span className="day-number">{day}</span>
              {daySessions.length > 0 && (
                <div className="sessions-indicator">
                  <span className="sessions-count">{daySessions.length}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
